<template>
  <Modal ref="modalRef" width="40rem" @before-show="isPropertyListed = false">
    <template #body>
      <template v-if="!isPropertyListed">
        <View gap="xl" direction="column">
          <View gap="md" direction="column" align="center">
            <Text variant="h2">List property?</Text>
            <Text variant="body-large" color="secondary" align="center">
              Your property will be listed on the major syndication platforms and Tallo will handle new leads
            </Text>
          </View>

          <Hint v-if="propertyStore.images.length < 5" icon="alert-triangle" color="amber" type="error">
            <View gap="sm" direction="column">
              <Text variant="h5">Add more photos to attract renters</Text>
              <Text variant="body-medium">
                Properties with 5 or more quality photos tend to rent significantly faster. Consider adding more photos
                before listing.
              </Text>
            </View>
          </Hint>

          <Hint gap="md">
            <template #icon>
              <IconTile icon="lightbulb-02" size="small" color="yellow" />
            </template>

            <View gap="md" direction="column">
              <Text variant="body-medium">
                Prompt responses to inquiries provide the best experience for renters and increase your chances of
                finding the right tenant quickly. You can edit your listing or take it down at any time.
              </Text>
            </View>
          </Hint>

          <SyndicationSelection ref="syndicationSelectionRef" />

          <Card variant="primary" size="medium" class="contact-email-container">
            <View gap="md" direction="column">
              <Text variant="body-medium" color="primary">
                Can't find your preferred platform or already have a lead? Just email the details to us and we’ll take
                care of the rest
              </Text>

              <Card variant="primary" size="small" class="contact-email" @click="copyEmail">
                <View align="center" justify="center" gap="sm">
                  <Icon name="mail-01" size="small" />
                  <Text variant="label-medium">{{ email }}</Text>
                </View>
              </Card>
            </View>
          </Card>

          <WhatToExpectCard :items="whatToExpectNextItems" />
        </View>
      </template>

      <template v-else>
        <View direction="column" gap="lg" align="center" padding-block="lg">
          <IconTile icon="check-circle" size="large" shape="rounded" color="green" :colored-icon="true" />
          <Text variant="h2">Property listed successfully!</Text>
          <Text variant="body-large" color="secondary" align="center">
            Your listing will be visible on your selected rental websites within about an hour, though it could take up
            to 24 hours. Tallo will start the process as soon as someone shows interest, only contacting you if a
            showing needs confirmation or if a question arises
          </Text>
        </View>
      </template>
    </template>

    <template #footer>
      <View v-if="!isPropertyListed" gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button variant="primary" :loading="isLoading" @click="submit">List property</Button>
      </View>

      <Button v-else variant="primary" @click="redirectToDashboardAndClose">Done</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  Button,
  Card,
  Hint,
  Icon,
  IconTile,
  Modal,
  Text,
  View,
  WhatToExpectCard,
  WhatToExpectItem,
  useAlertsStore,
} from '@tallo/design-system';
import { AppRouteNames } from '@tallo/investor/navigation';
import { usePropertyStore } from '@tallo/investor/property';

import SyndicationSelection from './syndication-selection.vue';

const propertyStore = usePropertyStore();
const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const syndicationSelectionRef = ref<InstanceType<typeof SyndicationSelection>>();
const route = useRoute();
const router = useRouter();
const isLoading = ref(false);
const isPropertyListed = ref(false);

const whatToExpectNextItems: WhatToExpectItem[] = [
  {
    icon: 'link-01',
    title: `Unit will be posted to major platforms`,
    description:
      'Tallo will make sure your listing is up to date and accurate on all major syndication platforms like Zillow, HotPads, and Trulia.',
  },
  {
    icon: 'user-plus-01',
    title: `Tallo will handle inquiries`,
    description:
      "Tallo will qualify renters and find suitable times for showings. If there is a question Tallo can't answer it will be escalated to you.",
  },
  {
    icon: 'calendar',
    title: `You'll receive showing requests`,
    description:
      'After leads are qualified, questions answered and time is found for a showing, you will receive a showing request with all the renter information needed to make a decision.',
  },
];

const email = 'newlead@tallo.ai';

async function submit() {
  isLoading.value = true;

  const propertyId = propertyStore.property?.id;
  const syndicationData = syndicationSelectionRef.value?.getSyndicationData();

  propertyStore
    .startPropertyListing(propertyId!, syndicationData)
    .then(() => {
      isPropertyListed.value = true;
    })
    .catch((error) => {
      console.error('Error listing property:', error);
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function redirectToDashboardAndClose() {
  router.push({
    name: AppRouteNames.INVESTOR_PROPERTY_DASHBOARD,
    params: {
      propertyId: route.params.propertyId,
    },
  });
  modalRef.value?.close();
}

function copyEmail() {
  navigator.clipboard
    .writeText(email)
    .then(() => {
      alertsStore.success('Email copied to clipboard');
    })
    .catch((error) => {
      console.error('Failed to copy email: ', error);
    });
}

function close() {
  modalRef.value?.close();
}

defineExpose({
  modalRef,
});
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}

.contact-email-container {
  background-color: var(--t-color-neutral-200) !important;
}

.contact-email {
  cursor: pointer;
}
</style>
